import React, { CSSProperties, forwardRef } from 'react';
import { maskDocument } from 'utils/formatters';
import { getVariable } from 'whitelabel';

/**
 * As peças do comprovante, no layout do app
 * (apps/mobile/src/features/withdrawals/Comprovante.tsx): cabeçalho com a marca,
 * bloco do valor e seções de rótulo/valor.
 *
 * Marcação sem Chakra e com estilo inline de propósito. O PNG é gerado por
 * html-to-image, que clona o nó copiando o `getComputedStyle` de cada elemento e
 * desenha o resultado dentro de um `<img>` com data URL — que não carrega recurso
 * externo nenhum. Então webfont não sobrevive à captura e token de tema só
 * atrapalha: o comprovante é um documento, e o que está escrito aqui é
 * literalmente o que sai na imagem e no papel.
 */
const PAPER = '#F8F5F0';
const INK = '#1A1614';
const INK_SOFT = '#4B4239';
const INK_MUTED = '#8B7E70';
const HAIRLINE = 'rgba(26,22,20,0.08)';
const SANS = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const MONO = `ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace`;

const labelStyle: CSSProperties = {
  fontFamily: MONO,
  fontSize: '11px',
  lineHeight: '14px',
  letterSpacing: '1.6px',
  textTransform: 'uppercase',
  color: INK_MUTED,
  whiteSpace: 'nowrap',
  margin: 0,
};

export const valueStyle: CSSProperties = {
  flex: 1,
  marginLeft: '16px',
  textAlign: 'right',
  fontFamily: SANS,
  fontSize: '15px',
  lineHeight: '22px',
  fontWeight: 500,
  color: INK,
  wordBreak: 'break-word',
  margin: 0,
};

export const captionStyle: CSSProperties = {
  fontFamily: SANS,
  fontSize: '13px',
  lineHeight: '18px',
  letterSpacing: '0.1px',
  color: INK_MUTED,
  margin: 0,
};

/** Rótulo em caixa alta e mono, como o `Label` do app. */
export const Label: React.FC<{ color?: string; children?: React.ReactNode }> = ({ color, children }) => (
  <span style={color ? { ...labelStyle, color } : labelStyle}>{children}</span>
);

/** Linha rótulo/valor: rótulo à esquerda, valor alinhado à direita. */
export const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) return null;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px',
      }}
    >
      <Label>{label}</Label>
      <span style={valueStyle}>{value}</span>
    </div>
  );
};

export const Hairline = () => <div style={{ height: '1px', backgroundColor: HAIRLINE, margin: '24px 0' }} />;

export const Section: React.FC<{
  title: string;
  children?: React.ReactNode;
}> = ({ title, children }) => (
  <div>
    {/* Título na cor padrão do `Label` do app; os rótulos das linhas ficam no
        tom apagado, senão bloco e linha se confundem. */}
    <Label color={INK_SOFT}>{title}</Label>
    <div style={{ marginTop: '12px' }}>{children}</div>
  </div>
);

/** Texto vindo de payload de provedor: normaliza para string ou nada. */
export const text = (value?: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized.length ? normalized : null;
};

/**
 * `maskDocument` chama `.toUpperCase()`/`.replace()` no valor recebido, então um
 * número vindo do payload do provedor (os campos `numero*` do Sicoob, por
 * exemplo) derrubava a renderização inteira do comprovante. Converte antes de
 * mascarar.
 */
export const asDocument = (value?: unknown): string | null => {
  const raw = text(value);
  return raw ? maskDocument(raw) || null : null;
};

export const dateTime = (value?: unknown): string | null => {
  const raw = text(value);
  if (!raw) return null;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;

  return `${parsed.toLocaleDateString('pt-BR')} · ${parsed.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const brandName = () => text(getVariable('name')) ?? '';

/**
 * Moldura do comprovante. Largura fixa porque html-to-image mede o nó pelo
 * `clientWidth`: nó sem largura própria vira SVG de 0px, e aí a captura devolve
 * blob nulo em vez de imagem.
 */
export const Sheet = forwardRef<HTMLDivElement, { children?: React.ReactNode }>(({ children }, ref) => (
  <div
    ref={ref}
    style={{
      width: '480px',
      minWidth: '480px',
      maxWidth: '480px',
      padding: '24px 24px 28px',
      backgroundColor: PAPER,
      fontFamily: SANS,
      color: INK,
      boxSizing: 'border-box',
    }}
  >
    {children}
  </div>
));

/** Marca à esquerda, nome do documento à direita. */
export const ReceiptHeader: React.FC<{ title: string }> = ({ title }) => {
  const logo = getVariable('logo') as string | undefined;
  const brand = brandName();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {/*
        Sem `src`, html-to-image troca o atributo por string vazia e rejeita
        a captura inteira com um Event de erro — então marca sem logo cai no
        nome, nunca num `<img>` vazio.
      */}
      {logo ? (
        <img
          src={logo}
          alt={brand}
          style={{
            height: '28px',
            maxWidth: '140px',
            objectFit: 'contain',
            objectPosition: 'left center',
          }}
        />
      ) : (
        <span
          style={{
            fontFamily: SANS,
            fontSize: '17px',
            fontWeight: 700,
            letterSpacing: '-0.2px',
            color: INK,
          }}
        >
          {brand}
        </span>
      )}
      <Label color={INK_SOFT}>{title}</Label>
    </div>
  );
};

/** O bloco que o olho procura primeiro: quanto, e quando. */
export const ReceiptAmount: React.FC<{
  label: string;
  amount: string;
  caption?: string | null;
}> = ({ label, amount, caption }) => (
  <div
    style={{
      marginTop: '24px',
      border: `1px solid ${HAIRLINE}`,
      borderRadius: '4px',
      padding: '18px',
    }}
  >
    <Label>{label}</Label>
    <p
      style={{
        margin: '8px 0 0',
        fontFamily: SANS,
        fontSize: '28px',
        lineHeight: '32px',
        letterSpacing: '-0.5px',
        fontWeight: 700,
        color: INK,
      }}
    >
      {amount}
    </p>
    {caption && <p style={{ ...captionStyle, marginTop: '8px' }}>{caption}</p>}
  </div>
);

export const ReceiptFooter = () => {
  const brand = brandName();

  return (
    <p style={{ ...captionStyle, textAlign: 'center' }}>
      {brand ? `Comprovante gerado por ${brand}.` : 'Comprovante gerado pelo painel.'}
    </p>
  );
};
